import { action, computed, observable, toJS } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import kebabCase from 'lodash/kebabCase';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import {
    BaseBlock,
    BLOCK_TYPE_TO_LABEL
} from '~/models/blocks/base-block.model';
import { IQuestionBlockResponse } from '~/types/server.types';
import {
    IDateQuestion,
    IOption,
    IOptionableQuestion,
    FixedProjectLinkedQuestionType,
    FixedContactLinkedQuestionType,
    QuestionType,
    TOptionableQuestion,
    TQuestion
} from '~/types/builder.types';
import type { RootStore } from '~/stores/root.store';
import { IQuestionTypeConfig } from '~/components/Builder/BuilderSidebar/QuestionSidebar/question-sidebar.utils';
import {
    getOptionsForDropdownQuestion,
    getValidationType
} from '~/components/Builder/Block/QuestionBlock/question-block.utils';
import type { FlowRevision } from '~/models/flow-revision.model';
import { RequireAtLeastOne } from '@honeybook/hbui/dist/tokens/types';

const DEFAULT_DESCRIPTION = {
    checkbox: '<p>Choose any of the options that apply</p>',
    radio: '<p>Choose one of the options</p>',
    dropdown: ''
};

const DEFAULT_QUESTION_TYPE = QuestionType.ShortText;

export type QuestionBlockDataForServer = ReturnType<
    BaseBlock['getDataForServer']
> &
    Pick<IQuestionBlockResponse, 'type' | 'question'> & {
        question_type: TQuestion['question_type'];
    };

export type InitialQuestionBlockType = {
    question_type?: Extract<
        QuestionType,
        'upload_attachments' | 'open_question'
    >;
};

export class QuestionBlock extends BaseBlock {
    type: 'question_block';
    @observable question: TQuestion;
    @observable focusedOption: string;

    constructor(
        block: RequireAtLeastOne<
            IQuestionBlockResponse & InitialQuestionBlockType,
            'question' | 'question_type'
        >,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;

        // This comes from the backend usually
        if (block.question) {
            this.question = block.question;
        } else {
            // This is the initial question block from the builder
            this.question = {
                question_type: block.question_type || DEFAULT_QUESTION_TYPE,
                text: '',
                placeholder: '',
                is_required: false,
                _id: uuidv4()
            };
            // Assign temp connected_variable_id
            this.connected_variable_id = uuidv4();
        }
    }

    dndAcceptedTypes() {
        if (this.isLeadDetailsQuestion) {
            return null;
        }
        return super.dndAcceptedTypes();
    }

    @computed
    get isMultiChoice() {
        return this.question.question_type === QuestionType.MultiChoice;
    }

    @computed
    get sidebarContextBlock() {
        return this.isLeadDetailsQuestion
            ? this.calculateRootLevelParent()
            : this;
    }

    @computed
    get isLeadDetailsQuestion() {
        return this.calculateRootLevelParent()?.isLeadDetailsBlock();
    }

    @computed
    get isLeadDetailsClientEmailQuestion() {
        return (
            this.isLeadDetailsQuestion &&
            this.question.validation_type === 'email'
        );
    }

    @computed
    get label() {
        if (this.isLeadDetailsQuestion) {
            return this.isLeadDetailsClientEmailQuestion
                ? 'Client email'
                : 'Client name';
        }
        return BLOCK_TYPE_TO_LABEL[this.type];
    }

    @computed
    get shouldHidePlus() {
        return this.isLeadDetailsQuestion;
    }

    @computed
    get shouldExpandOnSelect() {
        return !this.isLeadDetailsQuestion;
    }

    @computed
    get isDeletable() {
        return !this.isLeadDetailsQuestion;
    }

    @computed
    get isShowLinkIndication() {
        return this.isLeadDetailsQuestion;
    }

    @computed
    get isProjectTypeQuestion() {
        return (
            this.question.connected_property ===
            FixedProjectLinkedQuestionType.ProjectType
        );
    }

    @computed
    get isLeadSourceQuestion() {
        return (
            this.question.connected_property ===
            FixedProjectLinkedQuestionType.LeadSource
        );
    }

    isDraggable() {
        return !this.isLeadDetailsQuestion;
    }

    hasFocusedChild(selected: boolean) {
        if (selected) {
            return false;
        }

        return !!this.focusedOption;
    }

    @action
    getAlteredQuestionDataByType(
        type: QuestionType,
        config: Pick<IQuestionTypeConfig, 'question'>,
        prevQuestion: TQuestion = this.question
    ): TQuestion {
        const currTypeOptionable = this.isOptionable();
        const nextTypeOptionable = this.isOptionable(type);
        const alteredQuestion = cloneDeep(prevQuestion);
        const prevQuestionType = alteredQuestion.question_type;

        if (
            alteredQuestion.question_type === QuestionType.Date &&
            type !== QuestionType.Date
        ) {
            this.removeDateTimeQuestionProps(<IDateQuestion>alteredQuestion);
        }

        // assign validation type
        Object.assign(
            config,
            merge(config, { question: { ...getValidationType(type) } })
        );

        // assign default dropdown options
        if (type === QuestionType.Dropdown) {
            Object.assign(
                config,
                merge(config, {
                    question: {
                        options: this.getConfigDefaultOptions(
                            config.question,
                            currTypeOptionable
                        )
                    }
                })
            );
        }

        // if current question is linked, and new config
        // has a different type - reset connected property
        if (this.isLinkedQuestion() && !this.isQuestionType(type)) {
            alteredQuestion.connected_property = '';
        }

        this.assignConfigPlaceholdersAndText(config, alteredQuestion, type);

        if (type === QuestionType.Date) {
            if (this.isLinkedToCustomField(config)) {
                // assign the name of the custom field
                Object.assign(alteredQuestion, {
                    start_date_text: config.question.text
                });
            } else {
                Object.assign(alteredQuestion, {
                    start_date_text: 'Start date',
                    start_time_text: 'Start time',
                    end_date_text: 'End date',
                    end_time_text: 'End time'
                });
            }

            Object.assign(alteredQuestion, {
                has_time: false,
                has_end_date: false
            });
        }

        // This condition handles the optionable question description
        if (nextTypeOptionable) {
            const questionType = alteredQuestion.question_type;
            if (
                type === QuestionType.Dropdown ||
                !currTypeOptionable ||
                (alteredQuestion as TOptionableQuestion).description ===
                    DEFAULT_DESCRIPTION[prevQuestionType]
            ) {
                (alteredQuestion as TOptionableQuestion).description =
                    DEFAULT_DESCRIPTION[questionType];
            }
        }

        // If it was, and stayed optionable, then return to retain options value
        if (currTypeOptionable && nextTypeOptionable) {
            return alteredQuestion;
        }

        // If it wasn't optionable and now is, init empty options,
        // And there are no options
        if (
            !currTypeOptionable &&
            nextTypeOptionable &&
            !(alteredQuestion as TOptionableQuestion).options
        ) {
            (alteredQuestion as TOptionableQuestion).options = [
                { option_id: uuidv4(), answer_text: '' },
                { option_id: uuidv4(), answer_text: '' },
                { option_id: uuidv4(), answer_text: '' },
                { option_id: uuidv4(), answer_text: '' }
            ];
            // If it was optionable and now isn't, remove options, description property
        } else if (currTypeOptionable && !nextTypeOptionable) {
            delete (alteredQuestion as TOptionableQuestion).options;
            delete (alteredQuestion as TOptionableQuestion).description;
        }

        return alteredQuestion;
    }

    @action
    private assignConfigPlaceholdersAndText(
        config: Pick<IQuestionTypeConfig, 'question'>,
        alteredQuestion: TQuestion,
        type: QuestionType
    ) {
        // configPlaceholder and configText should override alteredQuestion
        // when it's empty string or a linked question
        const {
            text: configText,
            placeholder: configPlaceholder,
            ...restOfConfig
        } = config.question ?? { text: '' }; // "text" is for making sure it's not undefined

        Object.assign(alteredQuestion, restOfConfig, {
            question_type: type,
            placeholder: this.isLinkedQuestion(config)
                ? configPlaceholder
                : alteredQuestion.placeholder || configPlaceholder,
            text: this.isLinkedQuestion(config)
                ? configText
                : alteredQuestion.text || configText
        });
    }

    @action
    private removeDateTimeQuestionProps(dateQuestion: IDateQuestion) {
        delete dateQuestion.start_date_text;
        delete dateQuestion.start_time_text;
        delete dateQuestion.end_date_text;
        delete dateQuestion.end_time_text;
        delete dateQuestion.has_time;
        delete dateQuestion.has_end_date;
    }

    @action
    updateCompanyConnectedQuestion(options) {
        (this.question as TOptionableQuestion).options =
            getOptionsForDropdownQuestion(options);
    }

    @action
    getQuestionOptionsLabelAlterData(
        optionId: string,
        label: string
    ): IOption[] {
        if (!('options' in this.question)) {
            return undefined;
        }

        return this.question.options.map(opt =>
            opt.option_id === optionId
                ? {
                      ...opt,
                      answer_text: label
                  }
                : opt
        );
    }

    @action.bound
    setQuestionOptionFocus(optionId: string) {
        this.focusedOption = optionId;
    }

    @action
    addAnswer() {
        const question = <TOptionableQuestion>this.question;

        question.options.push({
            answer_text: '',
            option_id: uuidv4()
        });
    }

    @action
    setQuestionOptions(options: IOption[]) {
        (this.question as TOptionableQuestion).options = options;
    }

    @computed
    get formattedOptions() {
        return (<TOptionableQuestion>this.question).options.map(opt => ({
            label: opt.answer_text,
            value: kebabCase(opt.answer_text)
        }));
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }
    }

    get answer() {
        return this.connectedVariable?.value;
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors }
        } = this.rootStore;

        if (!showErrors) {
            return {};
        }

        const errors: any = {};
        if (
            this.question.question_type !== QuestionType.Date &&
            !this.question.text
        ) {
            errors.text = true;
        } else if (this.question.question_type === QuestionType.Date) {
            this.validateDateQuestion(errors);
        }

        if (this.isOptionable()) {
            (this.question as IOptionableQuestion).options.forEach(option => {
                if (!option.answer_text) {
                    errors[option.option_id] = true;
                    errors.option = true;
                }
            });

            if (!(this.question as IOptionableQuestion).options?.length) {
                errors.noOptions = true;
            }
        }

        return errors;
    }

    private validateDateQuestion(errors: any) {
        const {
            start_date_text,
            has_time,
            has_end_date,
            start_time_text,
            end_date_text,
            end_time_text
        } = this.question as IDateQuestion;

        if (!start_date_text) {
            errors.start_date_text = true;
        }

        if (has_time && !start_time_text) {
            errors.start_time_text = true;
        }

        if (has_end_date && !end_date_text) {
            errors.end_date_text = true;
        }

        if (has_end_date && has_time && !end_time_text) {
            errors.end_time_text = true;
        }
    }

    getConfigDefaultOptions(
        question: Partial<TQuestion>,
        currTypeOptionable: boolean
    ) {
        const {
            companyStore: { currCompany }
        } = this.rootStore;

        switch (question?.connected_property) {
            case FixedProjectLinkedQuestionType.LeadSource:
                return getOptionsForDropdownQuestion(currCompany.lead_sources);
            case FixedProjectLinkedQuestionType.ProjectType:
                return getOptionsForDropdownQuestion(
                    this.rootStore.companyStore.currCompany.sortedProjectTypes
                );
            default: {
                return currTypeOptionable
                    ? (this.question as TOptionableQuestion).options
                    : [
                          { option_id: uuidv4(), answer_text: '' },
                          { option_id: uuidv4(), answer_text: '' },
                          { option_id: uuidv4(), answer_text: '' }
                      ];
            }
        }
    }

    isOptionable(type: QuestionType = null) {
        const typeToCheck = type || this.question.question_type;

        return [
            QuestionType.SingleChoice,
            QuestionType.MultiChoice,
            QuestionType.Dropdown
        ].includes(typeToCheck);
    }

    hasQuestionType() {
        return !!this.question.question_type;
    }

    isQuestionType(questionType) {
        return this.question.question_type === questionType;
    }

    isLinkedQuestion(config: Pick<IQuestionTypeConfig, 'question'> = null) {
        return config
            ? !!config?.question?.connected_property
            : !!this.question.connected_property;
    }

    isLinkedToCustomField(
        config: Pick<IQuestionTypeConfig, 'question'> = null
    ) {
        const connectedProperty = config
            ? config.question?.connected_property
            : this.question.connected_property;

        return (
            !!connectedProperty &&
            ![
                ...Object.values(FixedProjectLinkedQuestionType),
                ...Object.values(FixedContactLinkedQuestionType)
            ].includes(connectedProperty)
        );
    }

    // TODO: LAASOT SEDER IN DA RESPONSE
    getDataForServer(): QuestionBlockDataForServer {
        const questionData = {
            question_type: this.question.question_type,
            ...(this.hasQuestionType() ? toJS(this.question) : {})
        };

        return {
            ...super.getDataForServer(),
            type: this.type as 'question_block',
            question: questionData as TQuestion,
            question_type: this.question.question_type
        };
    }

    clone(): any {
        const clone = super.clone();
        clone.question = cloneDeep(clone.question);

        return clone;
    }
}
